import React, { useEffect, useState } from 'react';

import { washHub } from 'store';

import { useWashHubListener } from 'hooks';

import { useMutation, useQuery } from 'react-query';
import { fetchImpactReports, generateImpactReportCSV } from 'api/reports';

import { ImpactReportProps } from './ImpactReport.props';

import { jsPDF } from 'jspdf';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import ImpactReportView from './ImpactReport.view';
import html2canvas from 'html2canvas';

const ImpactReportContainer = (props: ImpactReportProps) => {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<number | undefined>(undefined);
  const [sites, setSites] = useState<string[]>();
  const [accountManagers, setAccountManagers] = useState<string[]>();
  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [urlRecord, setUrlRecord] = useState({});
  const [reportId, setReportId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const isUpdated = useUrlSharing(urlRecord, washHubId);
  const queryUrl = useQueryParams();

  useEffect(() => {
    const urlParams = {
      washHubId: String(washHubId),
      ...(reportId ? { reportId: `${reportId}` } : {}),
      ...(startDate ? { startDate: `${startDate.valueOf()}` } : {}),
      ...(endDate ? { endDate: `${endDate.valueOf()}` } : {}),
      ...(sites ? { sites: sites.join('-') } : {}),
      ...(accountManagers
        ? { accountManagers: accountManagers.join('-') }
        : {}),
    };
    setUrlRecord(urlParams);
  }, [washHubId, endDate, startDate, sites, accountManagers, reportId]);

  useEffect(() => {
    const {
      sites,
      accountManagers,
      startDate,
      endDate,
      reportId,
      siteId,
    } = queryUrl;
    sites && setSites(sites.split('-'));
    accountManagers && setAccountManagers(accountManagers.split('-'));
    startDate && setStartDate(new Date(+startDate));
    endDate && setEndDate(new Date(+endDate));
    reportId && setReportId(reportId);
    siteId && setSiteId(siteId);
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  const queryKey = [
    'fetch-impact-reports',
    page,
    sites?.join(''),
    accountManagers?.join(''),
    endDate,
    startDate,
    washHubId,
    sortBy,
    sort,
  ];

  const queryFn = () =>
    fetchImpactReports({
      generateImpact: true,
      washHubId,
      page,
      limit: 10,
      startDate,
      endDate,
      site: sites,
      ...(accountManagers && accountManagers.length ? { accountManagers } : {}),
      sortByField: sortBy,
      sort,
    });

  const options = {
    keepPreviousData: true,
    enabled: isWashHubIdValid,
  };

  const { data, isLoading, isFetching } = useQuery(queryKey, queryFn, options);

  const { mutate, data: csvData = '', isLoading: isLoadingCSV } = useMutation(
    async () => {
      const response = await generateImpactReportCSV({
        site: sites,
        accountManagers,
        generateImpact: true,
        washHub: washHubId,
        page,
        startDate,
        endDate,
        limit: 10,
      });

      return response;
    },
  );

  useWashHubListener(() => {
    setPage(1);
    setSites(undefined);
    setEndDate(undefined);
    setStartDate(undefined);
  });

  const handleDownload = () => {
    const grid = document.getElementById('impact-report-grid');

    if (!grid) {
      return;
    }

    html2canvas(grid).then((canvas) => {
      const pdf = new jsPDF('p', 'mm', '');

      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = imgWidth * 1.085516;

      pdf.addImage(canvas, 'PNG', 0, 0, imgWidth, imgHeight);

      const fileName = `impact-report-sites`;

      pdf.save(`${fileName}.pdf`);
    });
  };

  const reports = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const sortHandler = (field: string) => {
    setSortBy(field);
    const sortOrder = sort === undefined || sort === 1 ? -1 : 1;
    setSort(sortOrder);
  };

  if (!isUrlMapped) return null;

  const onApplyFilters = (data: string[], type: string) => {
    setPage(1);
    if (type === 'accountManagers') {
      setAccountManagers(data);
      return;
    }
    setSites(data);
  };

  const onClearFilters = () => {
    setPage(1);
    setSites(undefined);
    setAccountManagers(undefined);
  };

  return (
    <ImpactReportView
      reportId={reportId}
      siteId={siteId}
      reports={reports}
      pagination={pagination}
      startDate={startDate}
      endDate={endDate}
      venue={sites}
      setSites={setSites}
      setEndDate={setEndDate}
      setStartDate={setStartDate}
      isLoading={isLoading || isFetching}
      isLoadingCSV={isLoadingCSV}
      csvData={csvData}
      mutate={mutate}
      handleDownloadPdf={handleDownload}
      sortHandler={sortHandler}
      onApplyFilters={onApplyFilters}
      onClearFilters={onClearFilters}
    />
  );
};

export default ImpactReportContainer;
